import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentQuestionDetails, RespondentQuestionDetailsDTO } from "./respondentQuestionDetails";
import { ProgressPercentage, ProgressPercentageDTO } from "./progressPercentage";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
/* eslint-disable import/export */
export abstract class RestartSurveyRPC implements
  LeoRPC<RestartSurveyRPC.Request, RestartSurveyRPC.Response, RestartSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: RestartSurveyRPC.Request
  ): Promise<LeoRPCResult<RestartSurveyRPC.Response, RestartSurveyRPC.Errors.Errors>>;
}

export namespace RestartSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyLinkId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isRestartSurveyRPCRequest: boolean = true;
  
    readonly surveyLinkId: LeoUUID;
  
  
    constructor(
    surveyLinkId: LeoUUID
    ) {
      this.surveyLinkId = surveyLinkId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      return new Request(
        surveyLinkId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyLinkId: this.surveyLinkId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `RestartSurveyRPC(surveyLinkId='${this.surveyLinkId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  
  export namespace ResponseEnums {
    export namespace Questiondetails {
      export interface FirstQuestionDTO {
        case: string;
        respondentQuestionDetails: RespondentQuestionDetailsDTO;
        progressPercentage: ProgressPercentageDTO;
      }
      
      export class FirstQuestion {
        readonly isQuestiondetailsFirstQuestion: boolean = true;
      
        readonly respondentQuestionDetails: RespondentQuestionDetails;
        readonly progressPercentage: ProgressPercentage;
      
      
        constructor(
        respondentQuestionDetails: RespondentQuestionDetails, 
        progressPercentage: ProgressPercentage
        ) {
          this.respondentQuestionDetails = respondentQuestionDetails;
          this.progressPercentage = progressPercentage;
        }
      
        static fromDTO(dto: FirstQuestionDTO): FirstQuestion {
          const respondentQuestionDetails = RespondentQuestionDetails.fromDTO(getObject(dto, "respondentQuestionDetails", InvalidResponseQuestiondetailsFirstQuestionRespondentQuestionDetailsError));
          const progressPercentage = ProgressPercentage.fromDTO(getObject(dto, "progressPercentage", InvalidResponseQuestiondetailsFirstQuestionProgressPercentageError));
          return new FirstQuestion(
            respondentQuestionDetails,
            progressPercentage
          );
        }
      
        toDTO(): FirstQuestionDTO {
          const dto: FirstQuestionDTO = {
            case: "FIRST_QUESTION",
              respondentQuestionDetails: this.respondentQuestionDetails.toDTO(),
              progressPercentage: this.progressPercentage.toDTO()
          };
          return dto;
        }
        }
      
      FirstQuestion.prototype.toString = function toString(): string {
        return `Questiondetails.FIRST_QUESTION(respondentQuestionDetails='${this.respondentQuestionDetails}',progressPercentage='${this.progressPercentage}')`;
      };
      
      export interface ThankYouPageDTO {
        case: string;
        thankYouPageText: SurveyLocalizedTextDTO;
      }
      
      export class ThankYouPage {
        readonly isQuestiondetailsThankYouPage: boolean = true;
      
        readonly thankYouPageText: SurveyLocalizedText;
      
      
        constructor(
        thankYouPageText: SurveyLocalizedText
        ) {
          this.thankYouPageText = thankYouPageText;
        }
      
        static fromDTO(dto: ThankYouPageDTO): ThankYouPage {
          const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseQuestiondetailsThankYouPageThankYouPageTextError));
          return new ThankYouPage(
            thankYouPageText
          );
        }
      
        toDTO(): ThankYouPageDTO {
          const dto: ThankYouPageDTO = {
            case: "THANK_YOU_PAGE",
              thankYouPageText: this.thankYouPageText.toDTO()
          };
          return dto;
        }
        }
      
      ThankYouPage.prototype.toString = function toString(): string {
        return `Questiondetails.THANK_YOU_PAGE(thankYouPageText='${this.thankYouPageText}')`;
      };
      
      export interface WelcomePageDTO {
        case: string;
        welcomePageText: SurveyLocalizedTextDTO;
      }
      
      export class WelcomePage {
        readonly isQuestiondetailsWelcomePage: boolean = true;
      
        readonly welcomePageText: SurveyLocalizedText;
      
      
        constructor(
        welcomePageText: SurveyLocalizedText
        ) {
          this.welcomePageText = welcomePageText;
        }
      
        static fromDTO(dto: WelcomePageDTO): WelcomePage {
          const welcomePageText = SurveyLocalizedText.fromDTO(getObject(dto, "welcomePageText", InvalidResponseQuestiondetailsWelcomePageWelcomePageTextError));
          return new WelcomePage(
            welcomePageText
          );
        }
      
        toDTO(): WelcomePageDTO {
          const dto: WelcomePageDTO = {
            case: "WELCOME_PAGE",
              welcomePageText: this.welcomePageText.toDTO()
          };
          return dto;
        }
        }
      
      WelcomePage.prototype.toString = function toString(): string {
        return `Questiondetails.WELCOME_PAGE(welcomePageText='${this.welcomePageText}')`;
      };
      
    
      export type Questiondetails = FirstQuestion | ThankYouPage | WelcomePage;
    
      export type QuestiondetailsDTO = FirstQuestionDTO | ThankYouPageDTO | WelcomePageDTO;
    
      export const fromDTO = (dto: QuestiondetailsDTO): Questiondetails => {
        switch (dto.case) {
          case "FIRST_QUESTION":
              return FirstQuestion.fromDTO(dto as FirstQuestionDTO);
          case "THANK_YOU_PAGE":
              return ThankYouPage.fromDTO(dto as ThankYouPageDTO);
          case "WELCOME_PAGE":
              return WelcomePage.fromDTO(dto as WelcomePageDTO);
          default:
            throw new InvalidResponseQuestiondetailsError(
                `Case ${dto.case} is not valid case of Questiondetails.`
            );
        }
      };
    }
    
  }
  export interface ResponseDTO {
    questiondetails: ResponseEnums.Questiondetails.QuestiondetailsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isRestartSurveyResponse: boolean = true;
  
    readonly questiondetails: ResponseEnums.Questiondetails.Questiondetails;
  
  
    constructor(
    questiondetails: ResponseEnums.Questiondetails.Questiondetails
    ) {
      this.questiondetails = questiondetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const questiondetails = ResponseEnums.Questiondetails.fromDTO(getObject(dto, "questiondetails", InvalidResponseQuestiondetailsError));
      return new Response(
        questiondetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          questiondetails: this.questiondetails.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `RestartSurvey(questiondetails='${this.questiondetails}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `RestartSurveyRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `RestartSurveyRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `RestartSurveyRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    

    export type Errors = InvalidSurveyLinkId | SurveyIsClosed | SurveyAlreadySubmitted;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | SurveyIsClosedDTO | SurveyAlreadySubmittedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyLinkIdError extends RestartSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidResponseQuestiondetailsError extends RestartSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsError";
  }
}

export class InvalidResponseQuestiondetailsFirstQuestionError extends InvalidResponseQuestiondetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsFirstQuestionError";
  }
}

export class InvalidResponseQuestiondetailsFirstQuestionRespondentQuestionDetailsError extends InvalidResponseQuestiondetailsFirstQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsFirstQuestionRespondentQuestionDetailsError";
  }
}

export class InvalidResponseQuestiondetailsFirstQuestionProgressPercentageError extends InvalidResponseQuestiondetailsFirstQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsFirstQuestionProgressPercentageError";
  }
}

export class InvalidResponseQuestiondetailsThankYouPageError extends InvalidResponseQuestiondetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsThankYouPageError";
  }
}

export class InvalidResponseQuestiondetailsThankYouPageThankYouPageTextError extends InvalidResponseQuestiondetailsThankYouPageError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsThankYouPageThankYouPageTextError";
  }
}

export class InvalidResponseQuestiondetailsWelcomePageError extends InvalidResponseQuestiondetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsWelcomePageError";
  }
}

export class InvalidResponseQuestiondetailsWelcomePageWelcomePageTextError extends InvalidResponseQuestiondetailsWelcomePageError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestiondetailsWelcomePageWelcomePageTextError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends RestartSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends RestartSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseSurveyAlreadySubmittedError extends RestartSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
